import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { useList, errMsg } from "@/lib/api";
import { PageHeader, StatCard, Spinner, EmptyState, Money } from "@/components/common";
import { ScoreRing, ConfidenceChip, pct } from "@/pages/teacher/KpiPages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Trophy, TrendingUp, Sparkles, Medal, Eye, FileText, ShieldAlert, CheckCircle2,
  AlertTriangle, Plane, Clock, RefreshCcw, UserX, LifeBuoy, ClipboardCheck,
  MessageSquareText, Users, GraduationCap, Award, Star, Lock, Banknote,
} from "lucide-react";

const label = (k) => String(k).replace(/_/g, " ");

const NOTE_STYLES = {
  praise: "bg-[#D8F3DC] text-[#2D6A4F]",
  concern: "bg-[#FFEDEC] text-[#D90429]",
  action: "bg-[#E7F0FE] text-[#1D4ED8]",
  general: "bg-muted text-muted-foreground",
};

function FinancialCard({ fin, testId = "financial-card" }) {
  if (!fin) return null;
  return (
    <div className="rounded-lg border border-secondary/50 bg-card p-6" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <Lock className="h-4 w-4 text-secondary" />
        <h3 className="text-lg font-semibold">Financial KPI</h3>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FFEDEC] text-[#D90429]">
          Owner only
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
        {[
          ["Attributed revenue", fin.teacher_attributed_revenue],
          ["Course fees collected", fin.collected_music_course_fee],
          ["Admin fees collected", fin.collected_school_admin_fee],
          ["Commission payable", fin.teacher_commission_payable],
          ["Gross profit", fin.teacher_gross_profit],
        ].map(([l, v]) => (
          <div key={l}>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{l}</p>
            <Money value={v} className="font-bold text-base" />
          </div>
        ))}
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Commission rate</p>
          <p className="font-bold text-base font-mono">{pct(fin.teacher_commission_rate)}</p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-4">{fin.note}</p>
    </div>
  );
}

// ---------------------------------------------------------------- /admin/teacher-kpi
export function AdminTeacherKpiList() {
  const { data, loading } = useList("/admin/teacher-kpi");
  if (loading) return <Spinner full />;
  const rows = data?.teachers || [];
  return (
    <div data-testid="admin-teacher-kpi-page">
      <PageHeader title="Teacher KPI"
        description={data?.financial_included
          ? "Performance and financial contribution per teacher. Financial figures are never shown to teachers."
          : "Performance overview per teacher. Financial columns require owner or finance access."} />
      {rows.length === 0 ? <EmptyState text="No active teachers." /> : (
        <div className="space-y-3 stagger-in">
          {rows.map((r) => (
            <div key={r.teacher_id} className="rounded-lg border bg-card p-4 flex flex-wrap items-center gap-4 lift"
              data-testid={`admin-kpi-row-${r.teacher_id}`}>
              <span className={`w-9 text-center font-display font-bold ${r.rank === 1 ? "text-[#D4AF37]" : "text-muted-foreground"}`}>
                {r.rank === 1 ? <Trophy className="h-5 w-5 mx-auto" /> : `#${r.rank}`}
              </span>
              <div className="flex-1 min-w-[160px]">
                <p className="font-semibold text-sm">{r.full_name}</p>
                <ConfidenceChip value={r.ranking_confidence} />
              </div>
              <div className="text-right"><p className="text-xs text-muted-foreground uppercase tracking-wider">KPI</p>
                <p className="font-bold font-display">{r.kpi_score}</p></div>
              <div className="text-right hidden sm:block"><p className="text-xs text-muted-foreground uppercase tracking-wider">Reliability</p>
                <p className="font-bold font-display">{r.operational_quality_score ?? "—"}</p></div>
              <div className="text-right hidden md:block"><p className="text-xs text-muted-foreground uppercase tracking-wider">Points</p>
                <p className="font-bold font-display">{r.reward_points}</p></div>
              <div className="text-right hidden md:block"><p className="text-xs text-muted-foreground uppercase tracking-wider">Badges</p>
                <p className="font-bold font-display">{r.badge_count}</p></div>
              {r.financial && (
                <>
                  <div className="text-right hidden lg:block"><p className="text-xs text-muted-foreground uppercase tracking-wider">Revenue</p>
                    <Money value={r.financial.teacher_attributed_revenue} className="font-bold" /></div>
                  <div className="text-right hidden lg:block"><p className="text-xs text-muted-foreground uppercase tracking-wider">Gross profit</p>
                    <Money value={r.financial.teacher_gross_profit} className="font-bold" /></div>
                </>
              )}
              <div className="flex gap-1.5 shrink-0">
                <Button asChild size="sm" variant="outline" data-testid={`view-kpi-${r.teacher_id}`}>
                  <Link to={`/admin/teacher-kpi/${r.teacher_id}`}><Eye className="h-3.5 w-3.5 mr-1" />KPI</Link>
                </Button>
                <Button asChild size="sm" data-testid={`view-review-${r.teacher_id}`}>
                  <Link to={`/admin/teacher-review/${r.teacher_id}`}><FileText className="h-3.5 w-3.5 mr-1" />Review</Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- /admin/teacher-kpi/:id
export function AdminTeacherKpiDetail() {
  const { id } = useParams();
  const { data: kpi, loading } = useList(`/admin/teacher-kpi/${id}`);
  if (loading) return <Spinner full />;
  if (!kpi) return <EmptyState text="Teacher not found." />;
  const drop = kpi.dropout || {};
  const ops = kpi.operational || {};
  return (
    <div data-testid="admin-teacher-kpi-detail">
      <PageHeader title={kpi.teacher?.full_name || "Teacher KPI"}
        description="Full KPI breakdown over the last 12 months."
        actions={
          <Button asChild size="sm" data-testid="open-internal-review">
            <Link to={`/admin/teacher-review/${id}`}><FileText className="h-4 w-4 mr-1" />Internal review</Link>
          </Button>
        } />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 stagger-in">
        <ScoreRing score={kpi.kpi_score} label="Overall KPI score" testId="admin-kpi-ring"
          sub="Quality, retention, progression, exams & reliability." />
        <ScoreRing score={ops.score} label="Operational quality" testId="admin-ops-ring"
          sub={`Penalty ${ops.total_penalty ?? 0}${ops.penalty_capped ? " (capped)" : ""} · credit +${ops.replacement_credit ?? 0}`} />
        <div className="rounded-lg border bg-card p-5 flex flex-col justify-between lift" data-testid="admin-kpi-rewards">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Rewards</p>
            <Sparkles className="h-4 w-4 text-secondary" />
          </div>
          <p className="text-3xl font-bold font-display mt-2">{kpi.reward_points ?? 0} pts</p>
          <p className="text-xs text-muted-foreground mt-1">{(kpi.badges || []).length} badges earned</p>
        </div>
      </div>

      {kpi.financial && <div className="mt-6"><FinancialCard fin={kpi.financial} /></div>}

      <h3 className="text-xl font-semibold mt-10 mb-4">Metrics</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 stagger-in">
        <StatCard icon={Users} label="Students served" value={kpi.total_students_served ?? 0}
          sub={`${kpi.students_retained ?? 0} retained · ${pct(kpi.retention_rate)}`} testId="admin-kpi-served" />
        <StatCard icon={GraduationCap} label="New students" value={kpi.new_students_assigned ?? 0} testId="admin-kpi-new" />
        <StatCard icon={TrendingUp} label="Progressions" value={kpi.students_progressed ?? 0} testId="admin-kpi-progressions" />
        <StatCard icon={Award} label="Exams" value={kpi.students_entered_exam ?? 0}
          sub={`${kpi.exam_passed ?? 0} passed · ${pct(kpi.exam_pass_rate)} rate`} testId="admin-kpi-exams" />
        <StatCard icon={ClipboardCheck} label="Attendance" value={pct(kpi.attendance_rate)}
          sub={`${kpi.lessons_recorded ?? 0} lessons`} testId="admin-kpi-attendance" />
        <StatCard icon={Clock} label="Punctuality" value={pct(kpi.punctuality_rate)} testId="admin-kpi-punctuality" />
        <StatCard icon={MessageSquareText} label="Remark completion" value={pct(kpi.lesson_remark_completion_rate)} testId="admin-kpi-remarks" />
        <StatCard icon={Star} label="Exam distinctions" value={kpi.exam_passed_distinction ?? 0}
          sub={`${kpi.exam_passed_credit ?? 0} credits`} testId="admin-kpi-distinctions" />
        <StatCard icon={Plane} label="Leaves" value={kpi.teacher_leave_count ?? 0} testId="admin-kpi-leaves" />
        <StatCard icon={RefreshCcw} label="Reschedules" value={kpi.teacher_reschedule_count ?? 0}
          sub={`${kpi.teacher_time_change_count ?? 0} time changes`} testId="admin-kpi-reschedules" />
        <StatCard icon={UserX} label="No-shows" value={kpi.no_show_count ?? 0}
          sub={`${kpi.teacher_cancellation_count ?? 0} last-minute`} testId="admin-kpi-noshows" />
        <StatCard icon={LifeBuoy} label="Replacements" value={`${kpi.replacement_classes_completed ?? 0}/${kpi.replacement_classes_due ?? 0}`}
          sub={`Completion ${pct(kpi.replacement_completion_rate)}`} testId="admin-kpi-replacements" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
        <div className="rounded-lg border bg-card p-6" data-testid="admin-dropout-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Dropout (fair ranking)</h3>
            <ConfidenceChip value={drop.ranking_confidence || "none"} />
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Raw rate</p>
              <p className="text-xl font-bold font-display mt-1">{pct(drop.raw_dropout_rate)}</p></div>
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Adjusted (fair)</p>
              <p className="text-xl font-bold font-display mt-1">{pct(drop.adjusted_dropout_rate)}</p></div>
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Dropouts / served</p>
              <p className="text-xl font-bold font-display mt-1">{drop.dropout_count ?? 0} / {drop.total_students_served ?? 0}</p></div>
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Sample group</p>
              <p className="text-xl font-bold font-display mt-1 capitalize">{drop.sample_size_group || "—"}</p></div>
          </div>
          {drop.warning && (
            <div className="mt-4 flex items-start gap-2 text-xs rounded-md bg-[#FFF8DC] text-[#B8860B] p-3">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{drop.warning}
            </div>
          )}
        </div>
        <div className="rounded-lg border bg-card p-6" data-testid="admin-ops-events">
          <h3 className="text-lg font-semibold mb-4">Operational events</h3>
          {!(ops.events || []).length ? <EmptyState text="No operational events." /> : (
            <div className="divide-y">
              {ops.events.slice(0, 10).map((e, i) => (
                <div key={i} className="py-2 flex items-center gap-3 text-sm">
                  <span className="font-mono text-xs w-24 shrink-0">{e.date}</span>
                  <span className="flex-1 capitalize min-w-0 truncate">{label(e.type)} — <span className="text-muted-foreground normal-case">{e.detail}</span></span>
                  <span className={`font-mono text-xs font-bold ${e.points >= 0 ? "text-[#2D6A4F]" : "text-[#D90429]"}`}>
                    {e.points >= 0 ? `+${e.points}` : e.points}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- /admin/teacher-review/:id
export function AdminTeacherReview() {
  const { id } = useParams();
  const { data, loading, reload } = useList(`/admin/teacher-review/${id}`);
  const [note, setNote] = useState("");
  const [category, setCategory] = useState("general");
  const [saving, setSaving] = useState(false);
  if (loading) return <Spinner full />;
  if (!data) return <EmptyState text="Teacher not found." />;
  const { teacher, kpi, summary, notes } = data;

  const addNote = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/teacher-review/${id}/notes`, { note, category });
      toast.success("Internal note added");
      setNote(""); setCategory("general");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-teacher-review-page">
      <PageHeader title={`Internal review — ${teacher.full_name}`}
        description={teacher.email}
        actions={
          <Button asChild size="sm" variant="outline" data-testid="back-to-kpi">
            <Link to={`/admin/teacher-kpi/${id}`}><TrendingUp className="h-4 w-4 mr-1" />Full KPI</Link>
          </Button>
        } />

      <div className="flex items-start gap-2 rounded-md bg-[#FFEDEC] text-[#D90429] text-xs p-3 mb-6" data-testid="internal-only-banner">
        <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
        This report is internal only. Teachers can never see this page, these notes, or any financial figures.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-in">
        <StatCard icon={TrendingUp} label="KPI score" value={kpi.kpi_score ?? "—"} accent testId="review-kpi-score" />
        <StatCard icon={ShieldAlert} label="Reliability" value={kpi.operational?.score ?? "—"} testId="review-ops-score" />
        <StatCard icon={Users} label="Retention" value={pct(kpi.retention_rate)}
          sub={`${kpi.total_students_served ?? 0} students served`} testId="review-retention" />
        <StatCard icon={Medal} label="Rewards" value={`${kpi.reward_points ?? 0} pts`}
          sub={`${(kpi.badges || []).length} badges`} testId="review-rewards" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="rounded-lg border bg-card p-6" data-testid="review-strengths">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-[#2D6A4F]" />Strengths
          </h3>
          <ul className="space-y-2.5">
            {summary.strengths.map((s, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#2D6A4F] mt-1.5 shrink-0" />{s}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border bg-card p-6" data-testid="review-concerns">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[#B8860B]" />Concerns
          </h3>
          <ul className="space-y-2.5">
            {summary.concerns.map((s, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#B8860B] mt-1.5 shrink-0" />{s}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {kpi.financial && <div className="mt-6"><FinancialCard fin={kpi.financial} testId="review-financial-card" /></div>}

      <h3 className="text-xl font-semibold mt-10 mb-4">Internal notes</h3>
      <div className="rounded-lg border bg-card p-6 mb-4" data-testid="add-note-card">
        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_auto] gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs">Category</Label>
            <select data-testid="note-category-select" className="border rounded-md px-3 py-2 text-sm bg-card w-full"
              value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="general">General</option>
              <option value="praise">Praise</option>
              <option value="concern">Concern</option>
              <option value="action">Action item</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Note (internal only)</Label>
            <Textarea data-testid="note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Discussed punctuality in 1:1 on 10 June — improvement plan agreed" />
          </div>
          <Button onClick={addNote} disabled={saving || !note.trim()} data-testid="add-note-button">Add note</Button>
        </div>
      </div>
      {!notes.length ? <EmptyState text="No internal notes yet." /> : (
        <div className="space-y-3 stagger-in" data-testid="notes-list">
          {notes.map((n) => (
            <div key={n.id} className="rounded-lg border bg-card p-4" data-testid={`note-${n.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${NOTE_STYLES[n.category] || NOTE_STYLES.general}`}>
                  {n.category}
                </span>
                <span className="text-xs text-muted-foreground">{n.author_name} · {(n.created_at || "").slice(0, 10)}</span>
              </div>
              <p className="text-sm">{n.note}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- /admin/kpi-settings
function NumberField({ k, value, onChange, testId }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs capitalize">{label(k)}</Label>
      <Input data-testid={testId} type="number" step="any" value={value}
        onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function PenaltySettingsCard() {
  const { data, loading, reload } = useList("/admin/kpi-penalty-settings");
  const [draft, setDraft] = useState(null);
  if (loading) return <Spinner />;
  if (!data) return null;
  const numeric = Object.fromEntries(Object.entries(data).filter(([, v]) => typeof v === "number"));
  const cur = draft || numeric;
  const save = async () => {
    try {
      await api.put("/admin/kpi-penalty-settings", {
        values: Object.fromEntries(Object.entries(cur).map(([k, v]) => [k, Number(v)])),
      });
      toast.success("Penalty settings saved");
      setDraft(null); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-lg border bg-card p-6" data-testid="penalty-settings-card">
      <h3 className="text-lg font-semibold mb-1">Operational penalties</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Points deducted from the reliability score (100 max). Not all leave is punished equally —
        medical and well-planned leave carry the smallest penalty.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.entries(cur).map(([k, v]) => (
          <NumberField key={k} k={k} value={v} testId={`penalty-${k}`}
            onChange={(nv) => setDraft({ ...cur, [k]: nv })} />
        ))}
      </div>
      <Button className="mt-4" size="sm" onClick={save} disabled={!draft} data-testid="save-penalty-settings">
        Save penalties
      </Button>
    </div>
  );
}

function RewardSettingsCard() {
  const { data, loading, reload } = useList("/admin/kpi-reward-settings");
  const [draft, setDraft] = useState(null);
  if (loading) return <Spinner />;
  if (!data) return null;
  const cur = draft || { points: { ...data.points }, penalty_points: { ...data.penalty_points } };
  const set = (group, k, v) => setDraft({ ...cur, [group]: { ...cur[group], [k]: v } });
  const save = async () => {
    try {
      await api.put("/admin/kpi-reward-settings", { values: {
        points: Object.fromEntries(Object.entries(cur.points).map(([k, v]) => [k, Number(v)])),
        penalty_points: Object.fromEntries(Object.entries(cur.penalty_points).map(([k, v]) => [k, Number(v)])),
      }});
      toast.success("Reward settings saved");
      setDraft(null); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-lg border bg-card p-6" data-testid="reward-settings-card">
      <h3 className="text-lg font-semibold mb-1">Reward & penalty points</h3>
      <p className="text-xs text-muted-foreground mb-4">Points earned/lost in the teacher reward ledger.</p>
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-secondary mb-2">Rewards</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        {Object.entries(cur.points).map(([k, v]) => (
          <NumberField key={k} k={k} value={v} testId={`reward-${k}`} onChange={(nv) => set("points", k, nv)} />
        ))}
      </div>
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#D90429] mb-2">Penalties</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.entries(cur.penalty_points).map(([k, v]) => (
          <NumberField key={k} k={k} value={v} testId={`reward-pen-${k}`} onChange={(nv) => set("penalty_points", k, nv)} />
        ))}
      </div>
      <Button className="mt-4" size="sm" onClick={save} disabled={!draft} data-testid="save-reward-settings">
        Save rewards
      </Button>
    </div>
  );
}

const VISIBILITY_HELP = {
  teacher_can_see_own_score: "Teachers can open their own KPI page",
  teacher_can_see_own_rank: "Teachers see their own position (#n of N)",
  teacher_can_see_anonymous_leaderboard: "Teachers see the anonymous leaderboard",
  teacher_can_see_full_teacher_names: "Show real names on the teacher leaderboard (off = anonymous)",
  teacher_can_see_financial_amounts: "NOT recommended — financial KPI is owner-only by design",
  teacher_can_see_internal_review: "NOT recommended — internal reviews stay private",
  financial_contribution_score_enabled: "Include financial contribution in internal scoring",
};

function VisibilitySettingsCard() {
  const { data, loading, reload } = useList("/admin/kpi-visibility-settings");
  const [draft, setDraft] = useState(null);
  if (loading) return <Spinner />;
  if (!data) return null;
  const bools = Object.fromEntries(Object.entries(data).filter(([, v]) => typeof v === "boolean"));
  const cur = draft || bools;
  const save = async () => {
    try {
      await api.put("/admin/kpi-visibility-settings", { values: cur });
      toast.success("Visibility settings saved");
      setDraft(null); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-lg border bg-card p-6" data-testid="visibility-settings-card">
      <h3 className="text-lg font-semibold mb-1">Ranking & privacy</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Controls what teachers can see. Financial data never reaches teacher accounts regardless of these switches —
        it is stripped at the server.
      </p>
      <div className="space-y-3">
        {Object.entries(cur).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-4 py-1.5 border-b last:border-0">
            <div>
              <p className="text-sm font-medium capitalize">{label(k)}</p>
              <p className="text-xs text-muted-foreground">{VISIBILITY_HELP[k]}</p>
            </div>
            <Switch data-testid={`visibility-${k}`} checked={v}
              onCheckedChange={(nv) => setDraft({ ...cur, [k]: nv })} />
          </div>
        ))}
      </div>
      <Button className="mt-4" size="sm" onClick={save} disabled={!draft} data-testid="save-visibility-settings">
        Save visibility
      </Button>
    </div>
  );
}

export function KpiSettingsPage() {
  return (
    <div data-testid="kpi-settings-page">
      <PageHeader title="KPI settings"
        description="Tune penalty weights, reward points and teacher-facing privacy. Changes apply to all KPI calculations immediately." />
      <div className="space-y-6 max-w-5xl">
        <VisibilitySettingsCard />
        <PenaltySettingsCard />
        <RewardSettingsCard />
      </div>
    </div>
  );
}
